import { useState } from 'react';
import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import { addCalendarDays, fmtDateTime, generateSettlementEID } from '../../../lib/util';
import type { Case, RecommendationEvent } from '../../../domain/initial-cases';
import { StatusBadge } from '../../../components/StatusBadge';
import { personName } from '../../../auth/actorNames';
import { getCurrentUser, isAssignedAnalyst } from '../../../auth/useUser';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { useUIState } from '../../../state/contexts/UIStateContext';
import { unmetRequiredKyc, type UnmetKycItem } from '../../../domain/kyc-progress';

// #587 — WHEN A DECIDED CASE MAY BE DECIDED AGAIN, and the one state where it may.
//
// This tab used to be a straight either/or: `c.decision ?` showed the read-only
// panel, and everything that RECORDS a decision — the approver's two buttons and
// the analyst's two recommendation buttons — lived in the else arm. Since
// `cases.decision` is never cleared, a decided case could never be re-decided
// here, which was fine while nothing sent one back into vetting.
//
// #587 does. `recordCure` writes `cureStatus: 'CURED'` with `status: 'IN_REVIEW'`
// on a case whose `decision` stays UNSUCCESSFUL (clearing it would make
// `undeliveredNoticeReason` report the unsuccessful notice as never delivered).
// That combination matched NOTHING: not this tab, not any Overview quick action,
// not Cure & Appeals — which queries `cureStatus: 'OPEN'` and `status: 'APPEAL'`
// — and not the onboarding re-entry, which gates on the blob's own status. So a
// cured case was terminal: the registrant sent what was missing, the case
// "resumed vetting", and no control in the portal could record the outcome.
//
// THE GATE IS THE PAIR, not `cureStatus` alone. `CURED` on its own persists
// after the case has been re-decided, so gating on it would offer these controls
// for ever; the resumed-vetting STATUS is what makes the case genuinely
// undecided-again, and it moves the moment a re-decision lands. It is the same
// pair the customer portal derives its "Back in vetting" state from
// (`CURED_CASE_STATUS`, apps/portal/src/pages/Overview.tsx), and deliberately so:
// two surfaces answering "is this case back in vetting?" differently is the
// drift #574 and #579 each spent an issue removing.
function isResumedAfterCure(c: Case): boolean {
  return c.cureStatus === 'CURED' && c.status === 'IN_REVIEW';
}

// #776 — WHO RECOMMENDED, AND WHO DECIDED, BY NAME.
//
// The recommendation is structured now (`c.recommendation`, `recommendedBy`,
// `recommendedAt`, and the server-projected `recommenderName`), so nothing here
// reads `c.notes`: the old `notes.includes('APPROVE')` was true for a DENY
// marker on any case whose notes mentioned approval anywhere. Legacy markers
// stay in the notes as historical text and are not parsed.
//
// Shared with OverviewTab, which shows the same two lines after a decision —
// two surfaces wording the same fact differently is how #574/#579 started.
// `personName` never renders a bare UUID: a recommender A026 could not
// attribute reads "Recommender unknown (legacy)".
export function recommendationLine(c: Case): string | null {
  if (!c.recommendation) return null;
  const verb = c.recommendation === 'APPROVE' ? 'approve' : 'deny';
  if (!c.recommendedBy && !c.recommenderName) return `Recommended ${verb} — Recommender unknown (legacy)`;
  const who = personName(c.recommendedBy, c.recommenderName);
  return `Recommended ${verb} by ${who}${c.recommendedAt ? ` on ${fmtDateTime(c.recommendedAt)}` : ''}`;
}

// #797 — WHAT THE LATEST DECISION WAS TAKEN ON. A decision consumes the
// recommendation it was taken on: the API empties `c.recommendation` in the
// same write and appends a CONSUMED history event. So on a decided case the
// current-value fields say nothing about the decision, and "who recommended"
// is read from the history instead: the RECOMMENDED event before the latest
// CONSUMED one. The walk back stops at a WITHDRAWN, CLEARED or earlier
// CONSUMED event: each ended an earlier recommendation, so a RECOMMENDED event
// behind it is not the one this decision consumed. Null when the history has
// no consumption, i.e. a case decided before #776 recorded history; the
// callers then fall back to `recommendationLine`. A RECOMMENDED event with no
// actor is A026's legacy shape; a CONSUMED one with no RECOMMENDED since the
// last ending event is a recommendation A029 consumed that was never in the
// history (the notes-marker shape) — both "unknown (legacy)".
export function decidedOnLine(c: Case): string | null {
  const history = c.recommendationHistory ?? [];
  let consumed = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].event === 'CONSUMED') { consumed = i; break; }
  }
  if (consumed < 0) return null;
  const verb = history[consumed].recommendation === 'APPROVE' ? 'approve' : 'deny';
  let made: RecommendationEvent | undefined;
  for (let i = consumed - 1; i >= 0; i--) {
    const e = history[i].event;
    if (e === 'RECOMMENDED') { made = history[i]; break; }
    if (e === 'WITHDRAWN' || e === 'CLEARED' || e === 'CONSUMED') break;
  }
  if (!made || (!made.actorId && !made.actorName)) return `Recommended ${verb} — Recommender unknown (legacy)`;
  return `Recommended ${verb} by ${personName(made.actorId, made.actorName)} on ${fmtDateTime(made.occurredAt)}`;
}

// #797 — WHAT HOLDS FINAL: CHANGED BACK, for the two surfaces that offer it
// outside this tab (Overview's quick actions and the Cure & Appeals rows), in
// the order the API refuses: "Recommendation required" first, then "KYC
// incomplete".
//   * a recommendation. The decision under appeal consumed the one it was
//     taken on (and A029 consumed those older decisions left), so a CURRENT
//     recommendation on an appealed case is by construction one its agent
//     made after that decision — the new one the API requires.
//   * complete KYC (#772), unless the case is grandfathered: Final: Changed
//     approves the registrant. `unmetRequiredKyc` is the list this tab's own
//     hold prints.
// #781 — no current recommendation, so no decision: the API refuses one with
// "Recommendation required". #766 exports it so the case progress tracker's
// Recommendation stage asks the same question this tab's approver panel does.
export function needsRecommendation(c: Pick<Case, 'recommendation'>): boolean {
  return !c.recommendation;
}

export type FinalChangedHold =
  | { reason: 'recommendation' }
  | { reason: 'kyc'; unmet: UnmetKycItem[] };
export function finalChangedHold(c: Case): FinalChangedHold | null {
  if (needsRecommendation(c)) return { reason: 'recommendation' };
  const unmet = unmetRequiredKyc(c);
  return unmet.length > 0 ? { reason: 'kyc', unmet } : null;
}

// The approver of the decision on the case. Before #776 the unsuccessful path
// never wrote `approverId`, so a denial decided before it has none to name.
export function approverLine(c: Case): string {
  if (!c.approverId && !c.approverName) return 'Approver not recorded';
  return `Decided by ${personName(c.approverId, c.approverName)} on ${fmtDateTime(c.decisionDate)}`;
}

// #777 — why a reader who is not the case's OA/VA Agent cannot recommend, and
// what would let them. Only the agent can (the API refuses everyone else with a
// 403), so the way to recommend is to hold the case. The wording follows the
// Overview tab's controls: anyone who can edit may take an UNASSIGNED case with
// "Assign to me", and only an admin may reassign one held by somebody else.
function recommendHint(c: Case, canAdmin: boolean): string {
  if (!c.analystId) return 'Only the case\u2019s OA/VA Agent can recommend, and this case has none. Assign the case to yourself on the Overview tab to recommend it.';
  return canAdmin
    ? 'Only the case\u2019s OA/VA Agent can recommend. Reassign the case to yourself on the Overview tab to recommend it.'
    : 'Only the case\u2019s OA/VA Agent can recommend. Ask an admin to reassign the case to you to recommend it.';
}

function historyLine(e: RecommendationEvent): string {
  const verb = e.recommendation === 'APPROVE' ? 'approve' : 'deny';
  const who = e.actorId || e.actorName
    ? personName(e.actorId, e.actorName)
    : e.legacy && e.event === 'RECOMMENDED' ? 'Recommender unknown (legacy)' : 'Unknown user';
  switch (e.event) {
    case 'RECOMMENDED': return `Recommended ${verb} — ${who}`;
    case 'WITHDRAWN': return `Withdrew the ${verb} recommendation — ${who}`;
    case 'CLEARED': return `The ${verb} recommendation was cleared when the case returned to review`;
    // #797 — the actor is whoever recorded the decision; none on a row A029
    // wrote for a decision taken before #797.
    case 'CONSUMED': return e.actorId || e.actorName
      ? `The ${verb} recommendation was used by the decision — ${who}`
      : `The ${verb} recommendation was used by the decision taken on it`;
    case 'RETURNED_TO_REVIEW': return `The decided case returned to review — ${who}`;
  }
}

export function DecisionTab({ c }: { c: Case }) {
  const { updateCase, recommendCase, withdrawRecommendation } = useAppData();
  const { setModal } = useUIState();
  const user = getCurrentUser()!;
  const canApprove = user.canApprove;
  const [recNote, setRecNote] = useState('');
  const recommended = recommendationLine(c);
  // #776 — dual control against the RECOMMENDER, not only the assigned
  // analyst. `recommendedBy` is a users.id, so the comparison is against
  // `user.serverId` (CLAUDE.md B-2). The API refuses the decision either way;
  // this is so the approver is told why before they press anything.
  const youRecommended = !!c.recommendedBy && !!user.serverId && c.recommendedBy === user.serverId;
  // #777 — ONLY THE CASE'S OA/VA AGENT RECOMMENDS, admin or analyst alike, and
  // the API refuses anyone else. `isAssignedAnalyst` compares `c.analystId` (a
  // users.id) with `user.serverId`, never `user.id` (the sub — CLAUDE.md B-2).
  // The API refuses legal by the DERIVED role (admin > analyst > approver >
  // legal, `deriveAppRole`), i.e. only a legal-ONLY user. `isLegal` is true for
  // anyone holding bcid-legal among other roles, so it is not the mirror;
  // `canEdit || canApprove` is false exactly for that legal-only user.
  const canRecommend = isAssignedAnalyst(c.analystId, user) && (user.canEdit || user.canApprove);
  // The prior decision still shows above the controls when both render — an
  // approver re-deciding after a cure needs to see what was decided the first
  // time and on what reason codes, which is the case they are being asked to
  // reconsider.
  const reDeciding = isResumedAfterCure(c);
  const showControls = !c.decision || reDeciding;
  // #772 — the required KYC checks still short of their evidence minimum. The
  // API refuses an approval (and an APPROVE recommendation) while any is, with
  // "KYC incomplete"; saying so here, with the list, is so nobody presses a
  // button to find out. Denial is not held back. Empty on a grandfathered case.
  const unmetKyc = unmetRequiredKyc(c);
  const kycHold = unmetKyc.length > 0;
  const kycHoldNotice = (id: string) => kycHold && (
    <div id={id} data-testid="kyc-hold" style={{ background: C.warnLight, border: `1px solid ${C.warn}`, borderRadius: 8, padding: 12, fontSize: 12, marginBottom: 12 }}>
      <strong>Approval is held until every required KYC check meets its evidence minimum.</strong> Still short:
      <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
        {unmetKyc.map(u => <li key={u.key}>{u.label} — {u.progress}</li>)}
      </ul>
    </div>
  );

  // #776/#777 — the agent's own controls: their current recommendation with
  // Withdraw, or the two Recommend buttons. Shared by the vetting controls and,
  // since #781, the appeal panel: Final: Changed is a decision and needs a
  // current recommendation, so the agent recommends an appealed case here too.
  const agentRecommendControls = () => (
    recommended ? (
      // #776 — one recommendation at a time. Changing your mind is a
      // withdrawal (kept in the history) and then a new one, so the
      // record shows the change rather than one entry overwriting
      // another — the API refuses a second recommendation outright.
      <div>
        <div style={{ background: C.infoBg, borderRadius: 8, padding: 12, fontSize: 12 }} data-testid="current-recommendation">
          <strong>{recommended}</strong>
          {c.recommendationNote && <div style={{ color: C.textMuted, marginTop: 4 }}>{c.recommendationNote}</div>}
          <div style={{ marginTop: 8, color: C.success }}>✓ Recommendation submitted — awaiting approver decision</div>
        </div>
        <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginTop: 8 }} onClick={() => withdrawRecommendation(c.id)}>Withdraw recommendation</button>
      </div>
    ) : (
      <div>
        <label htmlFor="rec-note" style={{ fontSize: 11, color: C.textMuted }}>Recommendation note (optional)</label>
        <textarea id="rec-note" style={{ ...styles.textarea, marginBottom: 12 }} value={recNote} onChange={e => setRecNote(e.target.value)} />
        {kycHoldNotice('kyc-hold-recommend')}
        <div style={{ display: 'flex', gap: 12 }}>
          <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall, ...(kycHold ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }} disabled={kycHold} aria-describedby={kycHold ? 'kyc-hold-recommend' : undefined} onClick={() => { recommendCase(c.id, 'APPROVE', recNote.trim() || undefined); setRecNote(''); }}>Recommend: Approve</button>
          <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => { recommendCase(c.id, 'DENY', recNote.trim() || undefined); setRecNote(''); }}>Recommend: Deny</button>
        </div>
      </div>
    )
  );

  return (
    <div style={styles.card}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Decision & Determination</h3>
      {c.decision && (
        <div style={reDeciding ? { paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` } : undefined}>
          <div style={{ fontSize: 16, marginBottom: 8 }}>{reDeciding ? 'Previous decision' : 'Decision'}: <StatusBadge status={c.decision} /></div>
          <div style={{ fontSize: 13, color: C.textMuted }}>Date: {fmtDateTime(c.decisionDate)}</div>
          {c.reasonCodes.length > 0 && <div style={{ marginTop: 8 }}>{c.reasonCodes.map((r: string, i: number) => <div key={i} style={{ ...styles.tag, background: C.dangerBg, color: C.danger, marginBottom: 4 }}>{r}</div>)}</div>}
          {c.notes && <div style={{ marginTop: 8, fontSize: 13, color: C.textMuted }}>Notes: {c.notes}</div>}
          {/* #776 — who recommended and who decided, by name. Not on a cured
              case: its decision consumed the recommendation, and the fresh one
              shows with the controls below. #797 — the recommendation the
              decision was taken on comes from the history (decidedOnLine); a
              current one on a decided case was made after the decision, and
              shows under appeal below. */}
          {!reDeciding && (
            <div style={{ marginTop: 8, fontSize: 13 }} data-testid="decision-people">
              <div>{decidedOnLine(c) ?? recommended ?? 'No recommendation on record'}</div>
              <div>{approverLine(c)}</div>
            </div>
          )}
          {reDeciding && <div style={{ marginTop: 8, fontSize: 13 }}>{approverLine(c)}</div>}
          {reDeciding && (
            <div style={{ marginTop: 12, background: C.infoBg, borderRadius: 8, padding: 12, fontSize: 12 }}>
              <strong>The registrant cured this.</strong> They sent the information the unsuccessful
              notice said was missing, so the case went back into vetting. Reassess it against the
              reason codes above and record the outcome below — this replaces the decision, it does
              not sit alongside it.
            </div>
          )}
        </div>
      )}
      {/* #781 — UNDER APPEAL. Final: Changed (Cure & Appeals, or the
          Overview tab) overturns the decision, so it needs a current
          recommendation like any decision; the case's agent makes or withdraws
          it here. Final: Unchanged leaves the decision standing and needs none. */}
      {c.status === 'APPEAL' && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.border}` }} data-testid="appeal-recommendation">
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 12 }}>
            Under appeal. Final: Changed overturns the decision, so it needs a new recommendation from the case&apos;s OA/VA Agent, made since that decision (the decision used up the one it was taken on), and a different approver issues it. Final: Unchanged needs none, and uses up any recommendation it finds.
          </div>
          {canRecommend ? agentRecommendControls() : (
            <div>
              {!c.recommendation && (
                <div style={{ fontSize: 12 }} data-testid="awaiting-recommendation">
                  No current recommendation.
                  {user.canEdit && <div style={{ marginTop: 4 }} data-testid="recommend-hint">{recommendHint(c, user.canAdmin)}</div>}
                </div>
              )}
              {c.recommendation && youRecommended && (
                <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => withdrawRecommendation(c.id)}>Withdraw recommendation</button>
              )}
            </div>
          )}
        </div>
      )}
      {showControls && (
        <div>
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 16 }}>Dual-control: the case&apos;s OA/VA Agent submits the recommendation, a different Approver issues the final decision</div>
          {canRecommend ? (
            <div>
              {canApprove ? (
                // An admin working their own case recommends it like an
                // analyst, and cannot decide it: the API refuses the
                // recommender as approver, and the agent as approver too.
                <div style={{ background: C.warnLight, border: `2px solid ${C.warn}`, borderRadius: 12, padding: 16, color: C.warn, fontSize: 13, marginBottom: 12 }}>
                  ⚠ You are the OA/VA Agent on this case. You can submit the recommendation; a different approver must issue the final decision to maintain dual-control separation.
                </div>
              ) : (
                <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 12 }}>As the OA/VA Agent on this case, you can submit a recommendation. An approver will issue the final decision.</div>
              )}
              {agentRecommendControls()}
            </div>
          ) : !canApprove ? (
            // #777 — not the agent, so no Recommend. The recommendation (if
            // any) still shows, and its author keeps Withdraw after the case is
            // reassigned away from them, as the API allows.
            <div>
              {user.canEdit && <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 12 }} data-testid="recommend-hint">{recommendHint(c, user.canAdmin)}</div>}
              {recommended && (
                <div style={{ background: C.infoBg, borderRadius: 8, padding: 12, fontSize: 12 }} data-testid="current-recommendation">
                  <strong>{recommended}</strong>
                  {c.recommendationNote && <div style={{ color: C.textMuted, marginTop: 4 }}>{c.recommendationNote}</div>}
                </div>
              )}
              {recommended && youRecommended && (
                <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginTop: 8 }} onClick={() => withdrawRecommendation(c.id)}>Withdraw recommendation</button>
              )}
            </div>
          ) : youRecommended ? (
            <div>
              <div style={{ background: C.warnLight, border: `2px solid ${C.warn}`, borderRadius: 12, padding: 16, color: C.warn, fontSize: 13 }}>
                ⚠ You recommended this case. A different approver must issue the final decision to maintain dual-control separation.
              </div>
              {/* #777 — a recommender the case was reassigned away from can still withdraw. */}
              <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall, marginTop: 8 }} onClick={() => withdrawRecommendation(c.id)}>Withdraw recommendation</button>
            </div>
          ) : needsRecommendation(c) ? (
            // #781 — EVERY decision needs a current recommendation, and the API
            // refuses one without it ("Recommendation required"); #776 asked
            // this only of a case back after a cure, whose earlier
            // recommendation the cure cleared.
            <div style={{ background: C.infoBg, borderRadius: 8, padding: 12, fontSize: 12 }} data-testid="awaiting-recommendation">
              {reDeciding
                ? 'This case needs a fresh recommendation from its OA/VA Agent before it can be decided again.'
                : 'This case needs a recommendation from its OA/VA Agent before it can be decided.'}
              {user.canEdit && <div style={{ marginTop: 4 }} data-testid="recommend-hint">{recommendHint(c, user.canAdmin)}</div>}
            </div>
          ) : (
            <div>
              {recommended && (
                <div style={{ background: C.infoBg, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12 }} data-testid="current-recommendation">
                  <strong>Analyst Recommendation:</strong> {c.recommendation === 'APPROVE' ? '✓ Approve (Successful Vet)' : '✗ Deny (Unsuccessful Vet)'}
                  <div style={{ color: C.textMuted, marginTop: 4 }}>{recommended}</div>
                  {c.recommendationNote && <div style={{ color: C.textMuted, marginTop: 4 }}>{c.recommendationNote}</div>}
                </div>
              )}
              {kycHoldNotice('kyc-hold-decide')}
              <div style={{ display: 'flex', gap: 12 }}>
                <button style={{ ...styles.btn, ...styles.btnSuccess, ...(kycHold ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }} disabled={kycHold} aria-describedby={kycHold ? 'kyc-hold-decide' : undefined} onClick={() => {
                  const eid = generateSettlementEID(c.registrant);
                  // #587 — ONE PATCH, and it closes the cure alongside the
                  // decision. `cureStatus` moves CURED -> CLOSED in the same
                  // write for two reasons: these controls are gated on the
                  // cured pair, so leaving it would keep offering them; and the
                  // customer portal reads the same pair for its "Back in
                  // vetting" state, which must not outlive the vetting. Not a
                  // second `updateCase` — same-tick PATCHes are serialised per
                  // case (AppDataContext), and a decision that lands in two
                  // writes is two audit rows for one act (H-1).
                  //
                  // `DECISION_SUCCESSFUL`, not a new code: this IS the ordinary
                  // successful vet, reached by a different road. `afterCure`
                  // records the road, which is what makes "how many cures ended
                  // successfully?" answerable — the reportability #587 exists
                  // for — without a code the nine-file lockstep would have to
                  // carry.
                  updateCase(c.id, {
                    decision: 'SUCCESSFUL', decisionDate: new Date().toISOString(), status: 'SUCCESSFUL', eid, approverId: user.id,
                    revetDueAt: addCalendarDays(new Date(), 365).toISOString(), revetStatus: 'SCHEDULED',
                    settlementSyncStatus: 'PENDING_SYNC',
                    ...(reDeciding ? { cureStatus: 'CLOSED' } : {}),
                    action: { code: 'DECISION_SUCCESSFUL', detail: { eid, approverName: user.name, ...(reDeciding ? { afterCure: true } : {}) } },
                  });
                }}>Approve: Successful Vet</button>
                <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => setModal('unsuccessful-decision')}>Mark: Unsuccessful Vet</button>
              </div>
            </div>
          )}
        </div>
      )}
      {(c.recommendationHistory?.length ?? 0) > 0 && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.border}` }} data-testid="recommendation-history">
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: C.textSecondary }}>Recommendation history</div>
          {c.recommendationHistory!.map((e, i) => (
            <div key={i} style={{ fontSize: 12, color: C.textMuted, marginBottom: 2 }}>
              {fmtDateTime(e.occurredAt)} · {historyLine(e)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
